'use client'

import { Button } from '@heroui/button'
import { Card, CardBody } from '@heroui/card'
import { Skeleton } from '@heroui/skeleton'
import { useQuery } from '@tanstack/react-query'
import { useRouter } from 'next/navigation'
import { useEffect, useState } from 'react'
import { useMemo } from 'react'

import { ArrowRightUpIcon, Edit3Icon } from '@/components/shared/icons'
import { Typography } from '@/components/ui/typography'
import { TITLE_DRAFT_STORAGE_KEY } from '@/constants'
import { routes } from '@/routes'
import { useHomeStore } from '@/store'
import { useApi } from '@/trpc/react'


const FALLBACK_PROMPT = 'diego'

const DailyPrompt = () => {
  const api = useApi()
  const { prompt: storePrompt, promptDate, setPrompt } = useHomeStore()
  const [mounted, setMounted] = useState(false)
  const router = useRouter()

  const shouldGetNewDailyPrompt = useMemo(() => {
    if (!mounted) return false
    if (!storePrompt || !promptDate) return true
    const today = new Date()
    const promptDay = new Date(promptDate)
    today.setHours(0, 0, 0, 0)
    promptDay.setHours(0, 0, 0, 0)
    return promptDay < today
  }, [promptDate, mounted, storePrompt])

  const { data: randomPrompt, isLoading } = useQuery({
    ...api.prompt.getRandom.queryOptions(),
    enabled: shouldGetNewDailyPrompt,
  })

  useEffect(() => {
    if (randomPrompt && randomPrompt?.content !== storePrompt?.content) {
      setPrompt(randomPrompt)
    }
  }, [randomPrompt, storePrompt?.content, setPrompt])

  useEffect(() => {
    setMounted(true)
  }, [])

  const content =
    storePrompt?.content ?? randomPrompt?.content ?? FALLBACK_PROMPT

  const handleCardClick = () => {
    window.localStorage.setItem(TITLE_DRAFT_STORAGE_KEY, content)
    router.push(routes.createMoment)
  }

  return (
    <Card
      shadow="none"
      className={`col-span-2 cursor-pointer rounded-[36px] border border-primary-100 bg-primary-50`}
    >
      <CardBody className="p-6 pr-2 pt-2" onClick={handleCardClick}>
        <div className="mb-4 flex items-baseline justify-between">
          <Edit3Icon className="mb-2 text-2xl text-[#A1A1AA]" />
          <Button
            isIconOnly
            size="lg"
            variant="flat"
            className="rounded-full bg-white text-2xl"
            onPress={handleCardClick}
          >
            <ArrowRightUpIcon />
          </Button>
        </div>
        {isLoading || !mounted ? (
          <Skeleton className="mb-2 h-5 w-1/2 rounded-sm" />
        ) : (
          <Typography
            size="large"
            weight="regular"
            className="mb-2 pr-[80px] font-crimsonPro leading-[22px] tracking-[-0.18px] text-default-foreground"
          >
            {content}
          </Typography>
        )}
        <Typography
          size="xsmall"
          weight="regular"
          className="font-crimsonPro text-content4-foreground"
        >
          Prompt of the day
        </Typography>
      </CardBody>
    </Card>
  )
}

export default DailyPrompt
